import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  deleteV1CompaniesCompanyIdServiceClassesId,
  ModelError_Item,
  patchV1CompaniesCompanyIdServiceClassesId,
  postV1CompaniesCompanyIdServiceClass,
} from '@treadinc/horizon-api-spec';
import axios from 'axios';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import DataGridPager from '~components/DataGrid/DataGridPager';
import actionsColumn from '~components/DataTable/actionsColumn';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { ShakyErrorIndicator } from '~components/ErrorIndicators/ShakyErrorIndicator';
import { SimpleMenu } from '~components/Menu/SimpleMenu';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { MAX_API_PAGE_SIZE } from '~constants/consts';
import useCursorPagination from '~hooks/useCursorPagination/useCursorPagination';
import { ServiceClass } from '~hooks/useServiceClass/models';
import useServiceClass from '~hooks/useServiceClass/useServiceClass';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { paletteColorStyles } from '~utils/colors';
import { dateFormat } from '~utils/dateTime';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import ServiceClassForm, { ServiceClassFormHandler } from './ServiceClassForm';
import { ServiceClassDto, ServiceClassDtoAsyncError } from './serviceClassSchema';

type ServiceClassDialogState = {
  isOpen: boolean;
  isSubmitting: boolean;
  serviceClass?: ServiceClass;
};

export const ServiceClassManagement = observer(() => {
  const { userStore, toasterStore } = useStores();

  const companyId = getEffectiveUserCompanyId(userStore);
  const { getServiceClassesByCompanyId } = useServiceClass();

  const serviceClassFormRef = useRef<ServiceClassFormHandler>(null);
  const [serviceClassFormDialogState, setServiceClassFormDialogState] =
    useState<ServiceClassDialogState>({ isOpen: false, isSubmitting: false });
  const [serviceClassFormDialogAsyncErrors, setServiceClassFormDialogAsyncErrors] =
    useState<ServiceClassDtoAsyncError[]>([]);

  const [deleteServiceClassDialogState, setDeleteServiceClassDialogState] =
    useState<ServiceClassDialogState>({ isOpen: false, isSubmitting: false });

  const {
    goToPage: goToServiceClassesPage,
    isFetching: isFetchingServiceClasses,
    pagination,
    reloadCurrentPage: reloadServiceClassesPage,
    rows,
    startOver: startServiceClassesOver,
  } = useCursorPagination<ServiceClass>({
    fetchPage: ({ cursor, link, signal }) =>
      getServiceClassesByCompanyId(companyId, { cursor, link, signal }),
    limit: MAX_API_PAGE_SIZE,
    onFetchError: (error) => {
      try {
        connection.handleRequestError(
          error,
          t('error_messages.service_classes.failed_to_fetch'),
        );
      } catch {
        // handleRequestError already showed the toast
      }
    },
  });

  useEffect(() => {
    if (companyId) {
      void startServiceClassesOver();
    }
  }, [companyId]);

  const userPermissions = userStore.getPermissions();

  const handleCreateNewServiceClassClick = useCallback(() => {
    setServiceClassFormDialogState({ isOpen: true, isSubmitting: false });
  }, []);

  const handleDeleteServiceClassClick = useCallback((serviceClass: ServiceClass) => {
    setDeleteServiceClassDialogState({ isOpen: true, isSubmitting: false, serviceClass });
  }, []);

  const handleCloseServiceClassFormDialog = useCallback(() => {
    setServiceClassFormDialogState((state) => ({ ...state, isOpen: false }));
  }, []);

  const handleCloseDeleteServiceClassDialog = useCallback(() => {
    setDeleteServiceClassDialogState((state) => ({ ...state, isOpen: false }));
  }, []);

  const handleSubmitServiceClassFormDialog = useCallback(async () => {
    const formData = await serviceClassFormRef.current?.onSubmit();

    if (!formData) {
      return;
    }

    const serviceClassId = serviceClassFormDialogState.serviceClass?.id;

    try {
      setServiceClassFormDialogAsyncErrors([]);
      setServiceClassFormDialogState((state) => ({ ...state, isSubmitting: true }));

      if (serviceClassId) {
        const response = await patchV1CompaniesCompanyIdServiceClassesId({
          path: {
            'company-id': companyId,
            id: serviceClassId,
          },
          body: { name: formData.name, color: formData.color },
        });
        const parsed = ServiceClass.parse(response.data.data);
        const name = parsed.name;

        void reloadServiceClassesPage();
        toasterStore.push(
          alert(t('administration.service_class.updated', { name }), AlertTypes.success),
        );
      } else {
        const response = await postV1CompaniesCompanyIdServiceClass({
          path: { 'company-id': formData.company.value },
          body: { name: formData.name, color: formData.color },
        });
        const parsed = ServiceClass.parse(response.data.data);
        const name = parsed.name;

        void goToServiceClassesPage();
        toasterStore.push(
          alert(t('administration.service_class.created', { name }), AlertTypes.success),
        );
      }

      setServiceClassFormDialogState({ isOpen: false, isSubmitting: false });
    } catch (error) {
      setServiceClassFormDialogState((state) => ({ ...state, isSubmitting: false }));

      if (axios.isAxiosError(error)) {
        const errorsArray = (error.response?.data?.error?.errors ??
          []) as ModelError_Item[];

        const asyncErrors: ServiceClassDtoAsyncError[] = [];
        const formFieldInfoByFieldInError: Record<
          string,
          { fieldInForm: keyof ServiceClassDto; label: string }
        > = {
          name: { fieldInForm: 'name', label: t('form_fields.name') },
        };

        errorsArray.forEach((errorsArrayItem) => {
          const { field, message } = errorsArrayItem;

          const isValueTakenError = message.includes('has already been taken');
          const fieldInfo = formFieldInfoByFieldInError[field];

          if (isValueTakenError && fieldInfo) {
            asyncErrors.push({
              field: fieldInfo.fieldInForm,
              message: t('error_messages.field_taken', { field: fieldInfo.label }),
            });
          }
        });

        if (asyncErrors.length) {
          setServiceClassFormDialogAsyncErrors(asyncErrors);
        } else {
          connection.handleRequestError(
            error,
            serviceClassId
              ? t('error_messages.service_classes.failed_to_update')
              : t('error_messages.service_classes.failed_to_create'),
          );
        }
      } else {
        connection.handleRequestError(
          error,
          serviceClassId
            ? t('error_messages.service_classes.failed_to_update')
            : t('error_messages.service_classes.failed_to_create'),
        );
      }
    }
  }, [
    goToServiceClassesPage,
    companyId,
    reloadServiceClassesPage,
    serviceClassFormDialogState.serviceClass?.id,
  ]);

  const deleteServiceClass = useCallback(async () => {
    const serviceClassId = deleteServiceClassDialogState.serviceClass?.id;

    if (!serviceClassId) {
      return;
    }

    try {
      setDeleteServiceClassDialogState((state) => ({ ...state, isSubmitting: true }));

      await deleteV1CompaniesCompanyIdServiceClassesId({
        path: {
          'company-id': companyId,
          id: serviceClassId,
        },
      });

      void reloadServiceClassesPage();
      toasterStore.push(
        alert(t('administration.service_class.deleted'), AlertTypes.success),
      );
      setDeleteServiceClassDialogState({ isOpen: false, isSubmitting: false });
    } catch (error) {
      setDeleteServiceClassDialogState((state) => ({ ...state, isSubmitting: false }));
      connection.handleRequestError(
        error,
        t('error_messages.service_classes.failed_to_delete'),
      );
    }
  }, [
    companyId,
    deleteServiceClassDialogState.serviceClass?.id,
    reloadServiceClassesPage,
  ]);

  const columns = useMemo<Array<DataTableColumn<ServiceClass>>>(
    () => [
      {
        field: 'name',
        headerName: t('form_fields.name'),
        renderCell: (serviceClass) => (
          <Box
            component="span"
            data-test-id={`service-class-name-${serviceClass.id}`}
            sx={{
              ...paletteColorStyles(serviceClass.color),
              borderRadius: 1,
              display: 'inline-block',
              fontWeight: 600,
              px: 1,
              py: 0.5,
            }}
          >
            {serviceClass.name}
          </Box>
        ),
      },
      {
        field: 'createdAt',
        headerName: t('common.created_on'),
        renderCell: (serviceClass) => (
          <Tooltip
            title={`${t('common.last_update')}: ${dateFormat(serviceClass.updatedAt)}`}
          >
            <span>{dateFormat(serviceClass.createdAt)}</span>
          </Tooltip>
        ),
      },
      actionsColumn((serviceClass) => {
        const options = userPermissions.canDeleteServiceClass
          ? [
              {
                title: t('actions.delete'),
                icon: <Delete />,
                callBack: () => handleDeleteServiceClassClick(serviceClass),
              },
            ]
          : [];

        return (
          <>
            {userPermissions.canEditServiceClass && (
              <IconButton
                size="small"
                onClick={() => {
                  setServiceClassFormDialogState({
                    isOpen: true,
                    isSubmitting: false,
                    serviceClass,
                  });
                }}
              >
                <Edit />
              </IconButton>
            )}
            {options && (
              <SimpleMenu options={options}>
                <MoreHoriz />
              </SimpleMenu>
            )}
          </>
        );
      }),
    ],
    [rows],
  );

  useEffect(() => {
    if (!serviceClassFormDialogState.isOpen) {
      setServiceClassFormDialogAsyncErrors([]);
    }
  }, [serviceClassFormDialogState.isOpen]);

  return (
    <>
      <SettingsPageHeader
        title={t('administration.service_class.title')}
        description={t('settings.page_descriptions.service_classes')}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <SmallButton
          color="brandV2Yellow"
          disabled={!userPermissions.canCreateServiceClass}
          onClick={handleCreateNewServiceClassClick}
          startIcon={<Add />}
        >
          {t('administration.service_class.create')}
        </SmallButton>
      </Box>
      <Box data-test-id={'service-class-data-grid'}>
        <DataTable
          id="service-class-data-grid"
          columns={columns}
          rows={rows}
          loading={isFetchingServiceClasses}
          headerTrailingComponent={
            <DataGridPager
              isFetching={isFetchingServiceClasses}
              onNavigate={(link) => void goToServiceClassesPage(link)}
              pagination={pagination}
            />
          }
        />
      </Box>
      <Dialog
        isOpen={serviceClassFormDialogState.isOpen}
        preventCloseOnBackdropClick
        preventCloseOnEscapeKeyDown
      >
        <DialogHeader onClose={handleCloseServiceClassFormDialog}>
          {serviceClassFormDialogState.serviceClass
            ? t('administration.service_class.update')
            : t('administration.service_class.create')}
        </DialogHeader>

        <DialogContent>
          <ServiceClassForm
            ref={serviceClassFormRef}
            asyncErrors={serviceClassFormDialogAsyncErrors}
            serviceClass={serviceClassFormDialogState.serviceClass}
          />
        </DialogContent>

        <DialogActions>
          {Boolean(serviceClassFormDialogAsyncErrors.length) && (
            <ShakyErrorIndicator
              message={t('administration.users.user_form.invalid_data_error_messages')}
            />
          )}

          <SmallButton
            color="secondary"
            disabled={serviceClassFormDialogState.isSubmitting}
            onClick={handleCloseServiceClassFormDialog}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            loading={serviceClassFormDialogState.isSubmitting}
            onClick={handleSubmitServiceClassFormDialog}
          >
            {serviceClassFormDialogState.serviceClass
              ? t('actions.update')
              : t('actions.create')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>
      <ConfirmationDialog
        isBusy={deleteServiceClassDialogState.isSubmitting}
        isOpen={deleteServiceClassDialogState.isOpen}
        onClose={handleCloseDeleteServiceClassDialog}
        onConfirm={deleteServiceClass}
        title={t('administration.service_class.delete')}
      >
        <Typography>{t('administration.service_class.delete_description')}</Typography>
      </ConfirmationDialog>
    </>
  );
});
